import { showNotification } from '@/plugins/notification';

/**
 * Vuex actions for the "groupchannel" module.
 *
 * Fetches a single group channel's details, lists group channels, creates a
 * new group channel, and loads/paginates a user's following list. Each
 * action calls the API and commits the result via mutations in updates.js.
 */
export default {
  /** Fetch a single channel's details by id and store it as the current group channel data. */
  async fetchGroupChannelData({ commit }, id) {
    const response = await fetch(`/api/channels/${id}`);
    let data = await response.json();
    commit('setGroupChannelData', data);
  },

  /** Reset the current group channel data to an empty object. */
  async clearGroupChannelData({ commit }, id) {
    let data = {};
    commit('setGroupChannelData', data);
  },

  /** Fetch a page of group channels for the given owner (ownerType/ownerId) and replace the channel list. */
  async fetchGroupChannelsList({ commit }, payload) {
    let params = new URLSearchParams({
      limit: '15',
      ownerType: payload.ownerType,
      ownerId: payload.ownerId,
    }).toString();

    const response = await fetch(`/api/channels?${params}`);
    let data = await response.json();
    commit('setGroupChannelsList', data.results);
  },

  /** Create a new group channel via the API; on success stores it as the current group channel data, otherwise shows an error notification. */
  async fetchCreateGroupChannel({ commit }, payload) {
    let response = await fetch(`/api/channels`, {
      method: 'POST',
      body: JSON.stringify({
        id: payload.id,
        channelId: payload.channelId,
        name: payload.name,
        description: payload.description,
        avatarFileId: payload.avatarFileId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit('setGroupChannelData', data);
    }
    return response;
  },

  /** Fetch the first page of channels a user follows (optionally filtered by followType) and replace the following list/count; resets to empty on a non-JSON response, error, or exception. */
  async fetchFollowingList({ commit }, { userId, offset = 0, limit = 15, followType }) {
    try {
      // followType = followType === 'negative' ? 'monitor' : 'follow';
      let params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
      if (followType) params.set('followType', followType);
      const response = await fetch(`/api/users/${userId}/following-channels?${params.toString()}` , { credentials: 'include' });
      const ct = response.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await response.text().catch(() => '');
        console.error('[fetchFollowingList] Non-JSON response', { status: response.status, snippet: text.slice(0, 200) });
        commit('setFollowingList', []);
        commit('setFollowingCount', 0);
        return { results: [], count: 0 };
      }
      const data = await response.json();
      if (response.ok && data && Array.isArray(data.results)) {
        commit('setFollowingList', data.results);
        commit('setFollowingCount', data.count || 0);
        return data;
      } else {
        commit('setFollowingList', []);
        commit('setFollowingCount', 0);
        return { results: [], count: 0 };
      }
    } catch (e) {
      commit('setFollowingList', []);
      commit('setFollowingCount', 0);
      return { results: [], count: 0 };
    }
  },

  /** Fetch a subsequent page of a user's following list (with an offset) and append the deduplicated results to the existing list. */
  async fetchNextFollowingPage({ commit }, { userId, offset, limit = 15, only }) {
    try {
      let params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
      if (only) params.set('only', only);
      const response = await fetch(`/api/users/${userId}/following-channels?${params.toString()}` , { credentials: 'include' });
      const ct = response.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await response.text().catch(() => '');
        console.error('[fetchNextFollowingPage] Non-JSON response', { status: response.status, snippet: text.slice(0, 200) });
        return { results: [] };
      }
      const data = await response.json();
      if (response.ok && data && Array.isArray(data.results)) {
        commit('addToFollowingList', data.results);
        return data;
      } else {
        return { results: [] };
      }
    } catch (e) {
      return { results: [] };
    }
  },
};
